import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import Modal from '../components/common/Modal';
import { Button } from '../components/common/Button';
import SearchableSelect from '../components/common/SearchableSelect';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import { customerTicketService } from '../services/customerTicketService';
import { 
  CustomerTicket, 
  TicketAttachment,
  TICKET_TYPES, 
  TICKET_PRIORITIES, 
  TICKET_STATUSES 
} from '../types/customerTicket';
import { useCustomers } from '../hooks/useCustomers';
import { useCustomerTickets } from '../hooks/useCustomerTickets';
import AttachmentUploader from '../components/customerTickets/AttachmentUploader';
import AttachmentList from '../components/customerTickets/AttachmentList';
import toast from 'react-hot-toast';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const CustomerTickets = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const { tickets, loading, refresh } = useCustomerTickets(user?.organizationId);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAttachmentModal, setShowDeleteAttachmentModal] = useState(false);
  const [editingProject, setEditingProject] = useState<CustomerTicket | null>(null);
  const [selectedProject, setSelectedProject] = useState<CustomerTicket | null>(null);
  const [deletingProject, setDeletingProject] = useState<CustomerTicket | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState<{ attachment: TicketAttachment; ticketId: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [newTicketAttachments, setNewTicketAttachments] = useState<TicketAttachment[]>([]);
  const [replyAttachments, setReplyAttachments] = useState<TicketAttachment[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    customerId: '',
    type: 'question' as const,
    title: '',
    description: '',
    priority: 'medium' as const,
    customerNotes: '',
    internalNotes: '',
    dueDate: '',
    estimatedCompletionDate: ''
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.organizationId || !user?.id) return;

    if (!formData.customerId) {
      toast.error('Kérlek válassz ki egy ügyfelet!');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Kérlek add meg a címet!');
      return;
    }

    try {
      const customer = customers.find(c => c.id === formData.customerId);
      const customerName = customer?.type === 'organization' 
        ? customer.companyName 
        : `${customer?.lastName} ${customer?.firstName}`;

      const ticketId = await customerTicketService.createTicket({
        customerId: formData.customerId,
        customerName: customerName || 'Ismeretlen',
        organizationId: user.organizationId,
        type: formData.type,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: 'submitted',
        customerNotes: formData.customerNotes || undefined,
        internalNotes: formData.internalNotes || undefined,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        estimatedCompletionDate: formData.estimatedCompletionDate ? new Date(formData.estimatedCompletionDate) : undefined,
        createdBy: user.id,
        attachments: newTicketAttachments
      });

      toast.success('Ticket létrehozva');
      setShowCreateModal(false);
      resetForm();
      setNewTicketAttachments([]);
      refresh();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Hiba a létrehozás során');
    }
  };

  const handleDeleteClick = (project: CustomerTicket) => {
    setDeletingProject(project);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingProject) return;

    try {
      await customerTicketService.deleteProject(deletingProject.id);
      toast.success('Ticket törölve');
      setShowDeleteModal(false);
      setDeletingProject(null);
      refresh();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Hiba a törlés során');
    }
  };

  const handleEditClick = (project: CustomerTicket) => {
    setEditingProject(project);
    setFormData({
      customerId: project.customerId,
      type: project.type as any,
      title: project.title,
      description: project.description,
      priority: project.priority as any,
      customerNotes: project.customerNotes || '',
      internalNotes: project.internalNotes || '',
      dueDate: project.dueDate ? project.dueDate.toISOString().split('T')[0] : '',
      estimatedCompletionDate: project.estimatedCompletionDate ? project.estimatedCompletionDate.toISOString().split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject) return;

    try {
      await customerTicketService.updateProject(editingProject.id, {
        type: formData.type,
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        customerNotes: formData.customerNotes || undefined,
        internalNotes: formData.internalNotes || undefined,
        dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
        estimatedCompletionDate: formData.estimatedCompletionDate ? new Date(formData.estimatedCompletionDate) : undefined
      });

      toast.success('Projekt módosítva');
      setShowEditModal(false);
      setEditingProject(null);
      resetForm();
      refresh();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Hiba a módosítás során');
    }
  };

  const handleStatusChange = async (projectId: string, newStatus: CustomerTicket['status']) => {
    if (!user?.id) return;

    try {
      await customerTicketService.updateStatus(projectId, newStatus, user.id);
      toast.success('Státusz frissítve');
      refresh();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Hiba a státusz frissítése során');
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !user?.id || !replyMessage.trim()) return;

    try {
      const staffName = user.displayName || user.email || 'Munkatárs';

      await customerTicketService.addResponse(selectedProject.id, {
        message: replyMessage,
        createdBy: user.id,
        createdByName: staffName,
        isStaff: true,
        attachments: replyAttachments
      });

      // Mellékletek hozzáadása a tickethez is
      for (const attachment of replyAttachments) {
        await customerTicketService.addAttachment(selectedProject.id, attachment);
      }

      toast.success('Válasz elküldve!');
      setReplyMessage('');
      setReplyAttachments([]);
      refresh();
      
      // Refresh selected project
      const updatedProject = await customerTicketService.getProjectById(selectedProject.id);
      if (updatedProject) {
        setSelectedProject(updatedProject);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Hiba a válasz küldése során');
    }
  };

  const handleAttachmentAdded = (attachment: TicketAttachment) => {
    setNewTicketAttachments([...newTicketAttachments, attachment]);
  };

  const handleReplyAttachmentAdded = (attachment: TicketAttachment) => {
    setReplyAttachments([...replyAttachments, attachment]);
  };

  const handleAttachmentDeleteClick = (attachment: TicketAttachment, ticketId: string) => {
    setDeletingAttachment({ attachment, ticketId });
    setShowDeleteAttachmentModal(true);
  };

  const handleConfirmAttachmentDelete = async () => {
    if (!deletingAttachment) return;
    
    try {
      await customerTicketService.removeAttachment(deletingAttachment.ticketId, deletingAttachment.attachment.id);
      toast.success('Melléklet törölve');
      
      // Refresh selected project if in details modal
      if (selectedProject && selectedProject.id === deletingAttachment.ticketId) {
        const updatedProject = await customerTicketService.getProjectById(selectedProject.id);
        if (updatedProject) {
          setSelectedProject(updatedProject);
        }
      }
      
      // Refresh editing project if in edit modal
      if (editingProject && editingProject.id === deletingAttachment.ticketId) {
        const updatedProject = await customerTicketService.getProjectById(editingProject.id);
        if (updatedProject) {
          setEditingProject(updatedProject);
        }
      }
      
      setShowDeleteAttachmentModal(false);
      setDeletingAttachment(null);
      refresh();
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Hiba a melléklet törlése során');
    }
  };

  const resetForm = () => {
    setFormData({
      customerId: '',
      type: 'question',
      title: '',
      description: '',
      priority: 'medium',
      customerNotes: '',
      internalNotes: '',
      dueDate: '',
      estimatedCompletionDate: ''
    });
  };

  const filteredProjects = tickets.filter(project => {
    const matchesSearch = 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const matchesType = filterType === 'all' || project.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return '#3b82f6'; // blue-500
      case 'in_progress': return '#f59e0b'; // amber-500
      case 'waiting_customer': return '#8b5cf6'; // purple-500
      case 'completed': return '#10b981'; // green-500
      case 'cancelled': return '#6b7280'; // gray-500
      default: return '#6b7280';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800';
      case 'waiting_customer': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const stats = {
    total: tickets.length,
    submitted: tickets.filter(p => p.status === 'submitted').length,
    in_progress: tickets.filter(p => p.status === 'in_progress').length,
    waiting_customer: tickets.filter(p => p.status === 'waiting_customer').length,
    completed: tickets.filter(p => p.status === 'completed').length
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-6">
      <CommonPageHeader
        title="Ügyfél Ticketek"
        subtitle="Ügyfél kérések és support ticketek kezelése"
        icon={ClipboardDocumentListIcon}
      />

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Összes</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Beküldve</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.submitted}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Folyamatban</p>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats.in_progress}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Ügyfélre vár</p>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.waiting_customer}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Befejezve</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.completed}</p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Keresés cím, ügyfél vagy leírás alapján..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="all">Minden státusz</option>
            {Object.entries(TICKET_STATUSES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="all">Minden típus</option>
            {Object.entries(TICKET_TYPES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          {/* Create Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center space-x-2 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Új Ticket</span>
          </button>
        </div>
      </div>

      {/* Projects List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                Nincs találat
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Próbálj meg más keresési feltételeket
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all border-l-4"
                  style={{ borderLeftColor: getStatusColor(project.status) }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                          {project.title}
                        </h3>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(project.status)}`}>
                          {TICKET_STATUSES[project.status]}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPriorityBadgeClass(project.priority)}`}>
                          {TICKET_PRIORITIES[project.priority]}
                        </span>
                        <span className="px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded">
                          {TICKET_TYPES[project.type]}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <UserIcon className="h-4 w-4" />
                        <span>{project.customerName}</span>
                      </div>
                      
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        {project.description}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                        <span>Létrehozva: {project.createdAt.toLocaleDateString('hu-HU')}</span>
                        {project.dueDate && (
                          <span className="text-red-600 dark:text-red-400">
                            Határidő: {project.dueDate.toLocaleDateString('hu-HU')}
                          </span>
                        )}
                        {project.responses && project.responses.length > 0 && (
                          <span className="flex items-center space-x-1">
                            <ChatBubbleLeftRightIcon className="h-3 w-3" />
                            <span>{project.responses.length} válasz</span>
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="ml-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setShowDetailsModal(true);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 hover:text-primary-700 dark:bg-primary-500/10 dark:text-primary-400 dark:border-primary-500/30 dark:hover:bg-primary-500/20 dark:hover:border-primary-500/50 transition-colors"
                      >
                        👁️ Részletek
                      </button>
                      <button
                        onClick={() => handleEditClick(project)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30 dark:hover:bg-blue-500/20 dark:hover:border-blue-500/50 transition-colors"
                      >
                        ✏️ Szerkeszt
                      </button>
                      <button
                        onClick={() => handleDeleteClick(project)}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30 dark:hover:bg-red-500/20 dark:hover:border-red-500/50 transition-colors"
                      >
                        🗑️ Törlés
                      </button>
                      {project.status !== 'completed' && (
                        <button
                          onClick={() => handleStatusChange(project.id, 'completed')}
                          className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:text-green-700 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30 dark:hover:bg-green-500/20 dark:hover:border-green-500/50 transition-colors flex items-center gap-1"
                        >
                          <CheckCircleIcon className="h-3 w-3" />
                          <span>Lezár</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Új Ticket létrehozása"
        size="4xl"
      >
        <form onSubmit={handleCreate} className="p-6 space-y-4">
          {/* Alapadatok Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Alapadatok
                </h3>

                {/* Customer Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ügyfél <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    options={customers.map(customer => ({
                      value: customer.id,
                      label: customer.type === 'organization' 
                        ? customer.companyName 
                        : `${customer.lastName} ${customer.firstName}`
                    }))}
                    value={formData.customerId}
                    onChange={(value) => setFormData({ ...formData, customerId: value })}
                    placeholder="Válassz ügyfelet..."
                    searchPlaceholder="Keresés ügyfelek között..."
                    clearable={true}
                  />
                </div>

                {/* Type & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Típus
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(TICKET_TYPES).map(([key, label]) => (
                        <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prioritás
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(TICKET_PRIORITIES).map(([key, label]) => (
                        <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cím <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="pl. Dokumentum kérés"
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    placeholder="Részletes leírás..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Üzenetek és Megjegyzések Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Üzenetek és Megjegyzések
                </h3>

                {/* Customer Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Üzenet az ügyfélnek
                  </label>
                  <textarea
                    value={formData.customerNotes}
                    onChange={(e) => setFormData({ ...formData, customerNotes: e.target.value })}
                    rows={2}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="Ez az üzenet látható lesz az ügyfélnek..."
                  />
                </div>

                {/* Internal Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Belső megjegyzés
                  </label>
                  <textarea
                    value={formData.internalNotes}
                    onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                    rows={2}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="Csak munkatársak látják..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Határidők Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Határidők
                </h3>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Határidő
                    </label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Becsült befejezés
                    </label>
                    <input
                      type="date"
                      value={formData.estimatedCompletionDate}
                      onChange={(e) => setFormData({ ...formData, estimatedCompletionDate: e.target.value })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mellékletek Section */}
          {formData.customerId && (
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="p-4 flex-1">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    Mellékletek
                  </h3>
                  <AttachmentUploader
                    ticketId="new"
                    ticketTitle={formData.title || 'Új ticket'}
                    ticketType={formData.type}
                    customerId={formData.customerId}
                    customerName={customers.find(c => c.id === formData.customerId)?.type === 'organization' 
                      ? customers.find(c => c.id === formData.customerId)?.companyName || ''
                      : `${customers.find(c => c.id === formData.customerId)?.lastName || ''} ${customers.find(c => c.id === formData.customerId)?.firstName || ''}`}
                    organizationId={user?.organizationId || ''}
                    uploadedBy={user?.id || ''}
                    uploadedByName={user?.displayName || user?.email || ''}
                    uploadedByType="staff"
                    onAttachmentAdded={handleAttachmentAdded}
                  />
                  {newTicketAttachments.length > 0 && (
                    <div className="mt-4">
                      <AttachmentList
                        attachments={newTicketAttachments}
                        canDelete={false}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              Mégse
            </Button>
            <Button type="submit" variant="primary">
              <PlusIcon className="h-4 w-4 mr-2" />
              Létrehozás
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal - Ugyanaz mint a Create, csak update-tel */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingProject(null);
          resetForm();
        }}
        title="Ticket szerkesztése"
        size="4xl"
      >
        <form onSubmit={handleUpdate} className="p-6 space-y-4">
          {/* Alapadatok Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Alapadatok
                </h3>

                {/* Customer (disabled) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ügyfél
                  </label>
                  <input
                    type="text"
                    disabled
                    value={customers.find(c => c.id === formData.customerId)?.type === 'organization' 
                      ? customers.find(c => c.id === formData.customerId)?.companyName 
                      : `${customers.find(c => c.id === formData.customerId)?.lastName} ${customers.find(c => c.id === formData.customerId)?.firstName}`}
                    className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Type & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Típus
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(TICKET_TYPES).map(([key, label]) => (
                        <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prioritás
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Object.entries(TICKET_PRIORITIES).map(([key, label]) => (
                        <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cím <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Üzenetek és Megjegyzések Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ChatBubbleLeftRightIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Üzenetek és Megjegyzések
                </h3>

                {/* Customer Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Üzenet az ügyfélnek
                  </label>
                  <textarea
                    value={formData.customerNotes}
                    onChange={(e) => setFormData({ ...formData, customerNotes: e.target.value })}
                    rows={2}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>

                {/* Internal Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Belső megjegyzés
                  </label>
                  <textarea
                    value={formData.internalNotes}
                    onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                    rows={2}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Határidők Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ClockIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Határidők
                </h3>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Határidő
                    </label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Becsült befejezés
                    </label>
                    <input
                      type="date"
                      value={formData.estimatedCompletionDate}
                      onChange={(e) => setFormData({ ...formData, estimatedCompletionDate: e.target.value })}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mellékletek Section */}
          {editingProject && (
            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex">
                <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                  <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="p-4 flex-1 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Mellékletek
                  </h3>
                  
                  {/* Meglévő mellékletek */}
                  {editingProject.attachments && editingProject.attachments.length > 0 && (
                    <AttachmentList
                      attachments={editingProject.attachments}
                      onDelete={(attachment) => handleAttachmentDeleteClick(attachment, editingProject.id)}
                      canDelete={true}
                    />
                  )}

                  {/* Új fájlok feltöltése */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Új mellékletek hozzáadása
                    </p>
                    <AttachmentUploader
                      ticketId={editingProject.id}
                      ticketTitle={editingProject.title}
                      ticketType={editingProject.type}
                      customerId={editingProject.customerId}
                      customerName={editingProject.customerName}
                      organizationId={user?.organizationId || ''}
                      uploadedBy={user?.id || ''}
                      uploadedByName={user?.displayName || user?.email || ''}
                      uploadedByType="staff"
                      onAttachmentAdded={async (attachment) => {
                        try {
                          await customerTicketService.addAttachment(editingProject.id, attachment);
                          toast.success('Melléklet hozzáadva');
                          const updated = await customerTicketService.getProjectById(editingProject.id);
                          if (updated) {
                            setEditingProject(updated);
                          }
                          refresh();
                        } catch (error) {
                          console.error('Error adding attachment:', error);
                          toast.error('Hiba a melléklet hozzáadása során');
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingProject(null);
                resetForm();
              }}
            >
              Mégse
            </Button>
            <Button type="submit" variant="primary">
              <PencilSquareIcon className="h-4 w-4 mr-2" />
              Mentés
            </Button>
          </div>
        </form>
      </Modal>

      {/* Projekt részletek Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedProject(null);
          setReplyMessage('');
        }}
        title={selectedProject?.title || 'Projekt részletek'}
        size="6xl"
      >
        {selectedProject && (
          <div className="p-4 space-y-4">
            {/* Projekt információk */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedProject.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                    selectedProject.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                    selectedProject.status === 'waiting_customer' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {TICKET_STATUSES[selectedProject.status]}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {TICKET_TYPES[selectedProject.type]}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">•</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedProject.customerName}
                  </span>
                </div>
                <select
                  value={selectedProject.status}
                  onChange={(e) => handleStatusChange(selectedProject.id, e.target.value as any)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {Object.entries(TICKET_STATUSES).map(([key, label]) => (
                    <option key={key} value={key} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">{label}</option>
                  ))}
                </select>
              </div>
              
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                <strong>Leírás:</strong> {selectedProject.description}
              </p>
              
              {selectedProject.customerNotes && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-3">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                    Üzenet az ügyfélnek:
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {selectedProject.customerNotes}
                  </p>
                </div>
              )}

              {selectedProject.internalNotes && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-1">
                    Belső megjegyzés:
                  </p>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300">
                    {selectedProject.internalNotes}
                  </p>
                </div>
              )}
            </div>

            {/* Mellékletek */}
            {selectedProject.attachments && selectedProject.attachments.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <AttachmentList
                  attachments={selectedProject.attachments}
                  onDelete={(attachment) => handleAttachmentDeleteClick(attachment, selectedProject.id)}
                  canDelete={true}
                />
              </div>
            )}

            {/* Válaszok */}
            {selectedProject.responses && selectedProject.responses.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Beszélgetés</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedProject.responses.map((response) => (
                    <div
                      key={response.id}
                      className={`p-3 rounded-lg ${
                        response.isStaff
                          ? 'bg-gray-100 dark:bg-gray-700 mr-12'
                          : 'bg-primary-50 dark:bg-primary-900/20 ml-12'
                      } ${response.isInternal ? 'border-2 border-yellow-400 dark:border-yellow-600' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {response.createdByName} {response.isStaff ? '(Munkatárs)' : '(Ügyfél)'}
                          {response.isInternal && ' 🔒 Belső'}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {response.createdAt.toLocaleDateString('hu-HU')} {response.createdAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {response.message}
                      </p>
                      {response.attachments && response.attachments.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <AttachmentList
                            attachments={response.attachments}
                            canDelete={false}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Válasz írása */}
            {selectedProject.status !== 'completed' && selectedProject.status !== 'cancelled' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Válasz írása</h3>
                <form onSubmit={handleSendReply} className="space-y-3">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={4}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="Írja ide a válaszát az ügyfélnek..."
                    required
                  />
                  
                  {/* Mellékletek */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                    <AttachmentUploader
                      ticketId={selectedProject.id}
                      ticketTitle={selectedProject.title}
                      ticketType={selectedProject.type}
                      customerId={selectedProject.customerId}
                      customerName={selectedProject.customerName}
                      organizationId={user?.organizationId || ''}
                      uploadedBy={user?.id || ''}
                      uploadedByName={user?.displayName || user?.email || ''}
                      uploadedByType="staff"
                      onAttachmentAdded={handleReplyAttachmentAdded}
                      maxFiles={3}
                    />
                    {replyAttachments.length > 0 && (
                      <div className="mt-3">
                        <AttachmentList
                          attachments={replyAttachments}
                          canDelete={false}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" variant="primary">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                      Válasz küldése
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingProject(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Ticket törlése"
        deleteMessage={`Biztosan törölni szeretnéd a "${deletingProject?.title}" ticketet?`}
        deleteWarning="Ez a művelet nem vonható vissza. A ticket és az összes hozzá tartozó válasz véglegesen törlődik."
      />

      {/* Delete Attachment Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteAttachmentModal}
        onClose={() => {
          setShowDeleteAttachmentModal(false);
          setDeletingAttachment(null);
        }}
        onConfirm={handleConfirmAttachmentDelete}
        title="Melléklet törlése"
        deleteMessage={`Biztosan törölni szeretnéd a "${deletingAttachment?.attachment.name}" fájlt?`}
        deleteWarning="Ez a művelet nem vonható vissza. A fájl véglegesen törlődik a Firebase Storage-ból és a dokumentumtárból is."
      />
    </div>
  );
};

export default CustomerTickets;
